<template>
  <div class="catalog-form">
    <h1 id="init">
      <i class="fal fa-arrow-circle-left" @click="onCancel" title="Regresar"></i>
      <span v-if="user && user.id">{{user.name}} - Licencias</span>
      <span v-else-if="device && device.id">{{device.name}} [{{ device.uuid }}] - Licencias</span>
    </h1>

    <b-row class="licenses-actions" v-show="releaseOrResetActions">
      <div class="col">
        <b-button class="licenses-action" variant="light" size="lg"
          v-if="releaseLicensesButton"
          :disabled="!thereAreLicensesToRelease"
          @click="releaseLicenses"><i class="fal fa-book-dead"></i> Liberar licencias
        </b-button>
        <b-button class="licenses-action" variant="light" size="lg"
          v-if="resetDevicesButton && isDomainRoot"
          :disabled="!thereAreDevicesToReset"
          @click="resetDevices"><i class="fal fa-skull"></i> Eliminar dispositivos
        </b-button>
      </div>
    </b-row>
    <!-- Filtros para la tabla de licencias -->
    <b-row class="filters-section ml-3 mr-3 mt-2" cols-md="4" cols-sm="2" align-v="center" v-if="device && device.id">
      <div class="col" v-if="loadDomainFilter && !loadingDomains">
        <InputSelectorFilter
          :label="'Dominio'"
          :loadingData="loadingDomains"
          :defaultValue="domainFilter"
          :values="domains"
          @update-value="updateDomain"
        />
      </div>
      <div class="col" v-if="loadUserFilter">
        <InputSelectorFilter
          :label="'Usuario'"
          :loadingData="loadingUsers"
          :defaultValue="userFilter"
          :values="users"
          @update-value="updateUser"
          :disabled="users.length <= 0"
        />
      </div>
      <div class="col">
        <b-form-checkbox v-model="activeFilter" >Solo licencias activas</b-form-checkbox>
      </div>
      <div class="col">
        <b-button class="mr-3" variant="light" @click="clearFilters()" :disabled="loadingLicenses || loadingDomains || loadingUsers">Limpiar</b-button>
        <b-button class="mr-3" variant="primary" @click="loadLicenses(preparePageRequestForLicenses())" :disabled="loadingLicenses || loadingDomains || loadingUsers">Buscar</b-button>
      </div>
    </b-row>

    <div class="form-area full-width">
      <b-card class="form-section" v-if="(user && user.id) || (device && device.id)">
        <div v-if="licensesItems && licensesItems.length">
          <b-tooltip target="pagination-licenses" placement="top" triggers="hover">Cantidad de licencias: {{totalLicenses}}</b-tooltip>
          <b-pagination
            id="pagination-licenses"
            v-model="currentPage"
            :total-rows="totalLicenses"
            :per-page="PAGE_SIZE_TABLE"
            aria-controls="licenses-table"
            @change="changePage"
          ></b-pagination>
          <b-overlay
            :show="loadingLicenses"
            spinner-type="grow"
            >
            <b-table
              id="licenses-table"
              class="table table-hover"
              head-row-variant="primary"
              bordered
              responsive
              :fields="licenseColumns"
              :items="licensesItems"
              :per-page="PAGE_SIZE_TABLE"
              >
              <template #head(selected) v-if="releaseOrResetActions">
                <b-checkbox class="checkbox-select-license" @change="selectedAllLicenses" :checked="allLicenseSelected" :indeterminate="indeterminateSelected">
                </b-checkbox>
              </template>
              <template #cell(selected)="row" v-if="releaseOrResetActions">
                <b-checkbox class="checkbox-select-license" v-model="row.item._selected" @change="selectedLicense(row.item)"></b-checkbox>
              </template>
            </b-table>
          </b-overlay>
        </div>
        <div class="no-data" v-else-if="!loadingLicenses && licensesItems && !licensesItems.length">
          <h5 style="opacity: 0.4; margin: 0;">No se encontraron licencias relacionadas.</h5>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import CatalogsService from '@/services/CatalogsService'
import { BasicEntity, Domain, DomainCatalogFilters, User, UserCatalogFilters } from '@/types/Catalogs'
import { Device, DeviceStatus } from '@/types/catalogs/Devices'
import { License, LicenseFilters, LicenseGridItem } from '@/types/catalogs/Licenses'
import { PageRequest, SortDirection } from '@/types/Support'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import moment from 'moment'
import userInfoStore from '@/store/userInfo'

@Component({
  name: 'AdminLicensesList',
  components: {
    InputSelectorFilter
  }
})
export default class AdminLicensesList extends Vue {
  @Prop({ required: false })
  user: User

  @Prop({ required: false })
  device: Device

  @Prop({ required: false, default: false })
  releaseLicensesButton: boolean

  @Prop({ required: false, default: false })
  resetDevicesButton: boolean

  @Prop({ required: false, default: false })
  updateComponent: boolean

  @Prop({ required: false, default: false })
  domainsFilter: boolean

  @Prop({ required: false, default: false })
  usersFilter: boolean

  PAGE_SIZE_TABLE = 50
  MOBILE_UUID_SIZE = 16
  currentPage = 1
  totalLicenses: number = 0

  licenseColumns: any[] = []
  licenses: License[] = []
  licensesItems: LicenseGridItem[] =[]
  licensesIdSelected: number[] = []
  allLicenseSelected: boolean = false
  indeterminateSelected: boolean = false
  thereAreLicensesToRelease: boolean = false
  thereAreDevicesToReset: boolean = false
  loading: boolean = false
  loadingLicenses: boolean = false
  users: BasicEntity[] = []
  userFilter: BasicEntity = new BasicEntity(0, '')
  clearUserFilter: boolean = false
  loadingUsers: boolean = false
  domains: BasicEntity[] = []
  domainFilter: BasicEntity = new BasicEntity(-1, '')
  loadingDomains: boolean = false
  activeFilter: boolean = false

  created () {
    this.loadLicenses(this.preparePageRequestForLicenses())
    this.prepareColumnsForTable()

    if (this.loadDomainFilter && this.domains && this.domains.length <= 0) {
      this.loadDomains()
    }
    if (this.loadUserFilter) {
      this.loadUsers()
    }
  }

  /** Permissions */

  /**
   * @returns if the user can release licenses or reset devices, false otherwise.
   */
  get releaseOrResetActions () {
    return this.releaseLicensesButton || this.resetDevicesButton
  }

  /** Check if we need to load users for filters */
  get loadUserFilter () {
    return this.device && this.device.id && this.usersFilter
  }

  /** Check if we need to load domains for filters */
  get loadDomainFilter () {
    return this.device && this.device.id && this.domainsFilter
  }

  /** @returns if the current user belongs to the root domain */
  get isDomainRoot () {
    return userInfoStore.inRootDomain()
  }

  /** Validate if a device is of mobile type */
  isMobileDevice (device: Device) {
    return device.uuid.length <= this.MOBILE_UUID_SIZE
  }

  /** Init values */

  /**
   * Prepare columns for licenses table
   */
  prepareColumnsForTable () {
    this.licenseColumns = []

    // Column for checkboxes
    if (this.releaseOrResetActions) {
      this.licenseColumns.push({ key: 'selected', label: '', sortable: false, stickyColumn: true })
    }
    this.licenseColumns.push({ key: 'id', label: 'Id Licencia', sortable: true })
    // When the consult is from an user
    if (this.user && this.user.id) {
      let userColumns = [
        { key: 'deviceId', label: 'Id Dispostivio', sortable: true },
        { key: 'device', label: 'Dispostivio', sortable: false },
        { key: 'uuid', label: 'UUID', sortable: false }
      ]
      this.licenseColumns = this.licenseColumns.concat(userColumns)

      // When the consult is from a device
    } else if (this.device && this.device.id) {
      let deviceColumns = [
        { key: 'userId', label: 'Id Usuario', sortable: true },
        { key: 'user', label: 'Usuario', sortable: false }
      ]
      this.licenseColumns = this.licenseColumns.concat(deviceColumns)
    }

    // Will always show
    this.licenseColumns = this.licenseColumns.concat([
      { key: 'active', label: 'Estado', sortable: true },
      { key: 'acquiredDate', label: 'Fecha de asignación' },
      { key: 'releaseDate', label: 'Fecha de liberación' }
    ])
  }

  /** Loads data */

  /**
   * Clears previous license lists
   * and updates its values
   *
   * @param pageRequest with configurations
   */
  async loadLicenses (pageRequest: PageRequest<LicenseFilters>) {
    this.licenses = []
    this.licensesItems = []
    this.licensesIdSelected = []
    await this.getLicenses(pageRequest)
  }

  /**
   * Clears previous user lists
   * and updates its values
   *
   * @param pageRequest with configurations
   */
  async loadUsers () {
    let pageRequest = new PageRequest<UserCatalogFilters>()
    pageRequest.filters.domainId = this.domainFilter.id
    this.users = []
    await this.getUsers(pageRequest)
  }

  /**
   * Clears previous domains lists
   * and updates its values
   *
   * @param pageRequest with configurations
   */
  async loadDomains () {
    let pageRequest = new PageRequest<DomainCatalogFilters>()
    this.domains = []
    await this.getDomains(pageRequest)
  }

  /** @returns the current user domain */
  get getUserDomain () {
    return userInfoStore.userInfo.domain as BasicEntity
  }

  /**
   * Genertes a request page to GET licenses by API
   *
   * @returns configuration request
   */
  preparePageRequestForLicenses () {
    let pageRequest = new PageRequest<LicenseFilters>()
    pageRequest.size = this.PAGE_SIZE_TABLE
    pageRequest.sort = 'acquiredDate'
    pageRequest.sortOrder = SortDirection.DESC
    pageRequest.filters.domain = this.domainFilter.id
    pageRequest.filters.active = this.activeFilter
    if (this.user && this.user.id) {
      // console.log('Cargando licencias por usuario ...')
      pageRequest.filters.userId = this.user.id
    } else if (this.userFilter && this.userFilter.id) {
      pageRequest.filters.userId = this.userFilter.id
      pageRequest.filters.deviceId = this.device.id
    } else if (this.device && this.device.id) {
      // console.log('Cargando licencias por dispositivo ...')
      pageRequest.filters.deviceId = this.device.id
    }
    return pageRequest
  }

  /**
   * Calls the GET licenses resource with a request configuration
   *
   * @param pageRequest with configurations for the request
   */
  async getLicenses (pageRequest: PageRequest<LicenseFilters>) {
    this.loadingLicenses = true
    await CatalogsService.getLicenses(pageRequest)
      .then((response) => {
        this.totalLicenses = response.total
        this.licenses.push(...response.results)
        this.licensesItems.push(...response.results.map((license: License) => {
          return this.licenseGridItemFormat(license)
        }))
      })
      .finally(() => {
        this.loadingLicenses = false
      })
  }

  /**
   * Calls the GET user resource with a request configuration
   *
   * @param pageRequest with configurations for the request
   */
  async getUsers (pageRequest: PageRequest<UserCatalogFilters>) {
    this.loadingUsers = true
    await CatalogsService.getUsers(pageRequest)
      .then((response) => {
        this.users.push(...response.results.map((user: User) => {
          return { id: user.id, name: user.name } as BasicEntity
        }))

        if (this.users.length < response.total) {
          pageRequest.page++
          this.getUsers(pageRequest)
        }
      })
      .finally(() => {
        this.loadingUsers = false
      })
  }

  /**
   * Calls the GET domain resource with a request configuration
   *
   * @param pageRequest with configurations for the request
   */
  async getDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
    this.loadingDomains = true
    await CatalogsService.getDomains(pageRequest)
      .then((response) => {
        this.domains.push(...response.results.map((domain: Domain) => {
          return { id: domain.id, name: domain.name } as BasicEntity
        }))

        if (this.domains.length < response.total) {
          pageRequest.page++
          this.getDomains(pageRequest)
        }
      })
      .finally(() => {
        this.loadingDomains = false
      })
  }

  /**
   * Sets the domain filter with the value selected
   * and emits the change in the domain to the list filter
   *
   * @param domain selected
   */
  updateDomain (domain: BasicEntity) {
    this.domainFilter = domain
    this.loadUsers()
  }

  /**
   * Sets the user filter with the value selected
   *
   * @param user selected
   */
  updateUser (user: BasicEntity) {
    this.userFilter = user
  }

  /**
   * Clears the filter values
   */
  clearFilters () {
    this.userFilter = new BasicEntity(0, '')
    this.clearUserFilter = true
    this.domainFilter = new BasicEntity(-1, '')
    this.activeFilter = false
    this.loadLicenses(this.preparePageRequestForLicenses())
  }

  /**
   * Generates the license item row to fill the licenses table
   *
   * @param license to get values
   */
  licenseGridItemFormat (license: License) {
    let gridItem = new LicenseGridItem()
    gridItem.id = license.id
    gridItem.userId = license.user.id
    gridItem.user = license.user.name
    gridItem.deviceId = license.device.id
    gridItem.device = license.device.name
    gridItem.uuid = license.device.uuid
    gridItem.active = this.activeFormat(license)
    gridItem.acquiredDate = this.dateFormat(license.acquiredDate)
    gridItem.releaseDate = this.dateFormat(license.releaseDate)
    return gridItem
  }

  /** Action for buttons */

  /**
   * Actives or inactives all checkboxes from the license table
   *
   * @param checked value if must select all rows
   */
  selectedAllLicenses (checked: boolean) {
    this.licensesIdSelected = checked ? this.licensesItems.slice().map((license) => { return license.id }) : []
    this.licensesItems.forEach(it => {
      checked ? Vue.set(it, '_selected', true) : Vue.set(it, '_selected', false)
    })
  }

  /**
   * Handles the change value from a checkbox on the row in the table
   *
   * @param item license grid selected
   */
  selectedLicense (item: LicenseGridItem) {
    let selected = this.licensesIdSelected
    item._selected ? selected.push(item.id) : selected.splice(selected.indexOf(item.id), 1)
  }

  /**
   * Emits the event to release (delete) licenses
   * sending a list of licenses to release (these ones must be active).
   */
  releaseLicenses () {
    let licensesToRelease = this.licenses.filter(it => this.licensesIdSelected.includes(it.id) && it.active)
    this.$emit('release-licenses', licensesToRelease)
  }

  /**
   * Emits the event to reset(delete) devices
   * related to the licenses selected
   * sending a list of devices to reset (these ones must be active)
   */
  resetDevices () {
    let licensesToRelease = this.licenses.filter(it => this.licensesIdSelected.includes(it.id))
    let devicesToReset = licensesToRelease.map(license => { return license.device })
    let alreadyListed: number[] = []
    // only mobile devices are considered
    devicesToReset = devicesToReset.filter(device => {
      if (this.isMobileDevice(device) &&
      !alreadyListed.includes(device.id) &&
      device.status === DeviceStatus.active) {
        alreadyListed.push(device.id)
        return true
      }
      return false
    })
    this.$emit('reset-devices', devicesToReset)
  }

  /**
   * On change page from paginator
   *
   * @param page - current page index
   */
  changePage (page: number) {
    let pageRequest = this.preparePageRequestForLicenses()
    this.currentPage = page
    pageRequest.page = page - 1
    this.selectedAllLicenses(false)
    this.allLicenseSelected = false
    this.loadLicenses(pageRequest)
  }

  /**
   * To close the licenses view
   */
  onCancel () {
    this.$emit('close')
  }

  /** Formats */

  /**
   * Generates the iso date format to render in the table license rows
   *
   * @param date to format
   * @returns ISO 8601 format date as string
   */
  dateFormat (date?: Date) {
    return date ? moment(date).format('DD/MM/YYYY').toString() : '- -/- -/- - - -'
  }

  /**
   * Generates a string format for the 'active' license value
   *
   * @param License to format its 'active' value
   * @returns string
   */
  activeFormat (license: License) {
    return license.active && !license.releaseDate ? 'Activa' : ' Inactiva'
  }

  /** Watchers */

  /**
   * When 'updateComponent' is active, we must reload the table rows.
   */
  @Watch('updateComponent')
  updateLicenses () {
    if (this.updateComponent) {
      this.loadLicenses(this.preparePageRequestForLicenses())
      this.$emit('updated-component')
    }
  }

  /**
   * Checks the changes on the firts column, checkboxes,
   * to enable/disable the action buttons.
   */
  @Watch('licensesIdSelected')
  checkLicensesToRelease () {
    let licensesSelected = this.licenses.filter(it => this.licensesIdSelected.includes(it.id))
    let licensesSelectedActive = licensesSelected.filter(it => it.active)
    this.thereAreLicensesToRelease = licensesSelectedActive.length > 0
    // filter by the devices that are mobiles and aren't like resettle
    this.thereAreDevicesToReset = licensesSelected.filter(license =>
      this.isMobileDevice(license.device) &&
      license.device.status !== DeviceStatus.resettle).length > 0

    // States for select checkbox
    if (this.licensesIdSelected.length === 0) {
      this.indeterminateSelected = false
      this.allLicenseSelected = false
    } else if (this.licensesIdSelected.length === this.licensesItems.length) {
      this.indeterminateSelected = false
      this.allLicenseSelected = true
    } else {
      this.indeterminateSelected = true
      this.allLicenseSelected = false
    }
  }
}

</script>

<style lang="scss" scoped>

$gray-500: #adb5bd !default;

.licenses-actions {
  margin: 24px 10px 0 32px;
  width: fit-content;
  padding: 8px;
  background-color: #fff;
  border-radius: 3px;
  box-shadow: 0 0 5px 2px #ddd;

  .col {
    padding: 0;
  }

  .licenses-action {
    height: 48px;
    border-right: 1px solid #ddd;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 300;
    i {
      margin: 0 3px;
      font-size: 1.1rem;
    }
    &:hover, &.active {
      background-color: #f0f0f0;
    }
    &.disabled {
      opacity: 0.35;
      pointer-events: none;
    }
  }
}

::v-deep .page-item.active .page-link {
  z-index: 0;
}

::v-deep .table-responsive > .table.b-table > thead > tr > .b-table-sticky-column{
  z-index: 1;
}
::v-deep .table-responsive > .table.b-table > tbody > tr > .b-table-sticky-column {
  z-index: 1;
}
</style>
